/**
 * Generic duck for listing collections (see util/listingCollections.js).
 *
 * State is keyed by collection id, so the landing page, the listing page, and any future
 * page can share one fetch path:
 *   { justListed: { listingIds: [UUID], inProgress: false, error: null } }
 */
import { storableError } from '../util/errors';
import * as log from '../util/log';
import {
  COLLECTION_SOURCES,
  buildCollectionQueryParams,
  rankBySimilarity,
} from '../util/listingCollections';
import { addMarketplaceEntities } from './marketplaceData.duck';

// ================ Action types ================ //

export const FETCH_LISTING_COLLECTION_REQUEST =
  'app/listingCollections/FETCH_LISTING_COLLECTION_REQUEST';
export const FETCH_LISTING_COLLECTION_SUCCESS =
  'app/listingCollections/FETCH_LISTING_COLLECTION_SUCCESS';
export const FETCH_LISTING_COLLECTION_ERROR =
  'app/listingCollections/FETCH_LISTING_COLLECTION_ERROR';

// ================ Reducer ================ //

const initialState = {};

const collectionState = (state, id) =>
  state[id] || { listingIds: [], inProgress: false, error: null };

export default function listingCollectionsReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_LISTING_COLLECTION_REQUEST:
      return {
        ...state,
        [payload.id]: { ...collectionState(state, payload.id), inProgress: true, error: null },
      };
    case FETCH_LISTING_COLLECTION_SUCCESS:
      return {
        ...state,
        [payload.id]: { listingIds: payload.listingIds, inProgress: false, error: null },
      };
    case FETCH_LISTING_COLLECTION_ERROR:
      return {
        ...state,
        [payload.id]: { listingIds: [], inProgress: false, error: payload.error },
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchListingCollectionRequest = id => ({
  type: FETCH_LISTING_COLLECTION_REQUEST,
  payload: { id },
});

export const fetchListingCollectionSuccess = (id, listingIds) => ({
  type: FETCH_LISTING_COLLECTION_SUCCESS,
  payload: { id, listingIds },
});

export const fetchListingCollectionError = (id, error) => ({
  type: FETCH_LISTING_COLLECTION_ERROR,
  payload: { id, error },
  error: true,
});

// ================ Selectors ================ //

export const selectListingCollection = (state, id) =>
  state.listingCollections?.[id] || { listingIds: [], inProgress: false, error: null };

// ================ Thunks ================ //

/**
 * Drop the listing being viewed from a collection response before it is normalised.
 *
 * The API has no "not this id" filter, so that listing comes back inside its own collections.
 * The resource object it comes back as is deliberately narrow - a capped 'limit.images' and thumbnail
 * variants - and normalising it would overwrite the full images relationship that showListing
 * already stored, leaving the gallery with a single image. It is filtered out of the collection
 * for display anyway, so it does not need to reach the entity store at all.
 */
const withoutExcludedListing = (response, excludeListingId) => {
  const listings = response?.data?.data;
  if (!excludeListingId || !Array.isArray(listings)) {
    return response;
  }
  return {
    ...response,
    data: {
      ...response.data,
      data: listings.filter(listing => listing.id?.uuid !== excludeListingId),
    },
  };
};

/**
 * Fetch a single collection.
 *
 * A failing collection never rejects: a collection that can't be queried (e.g. a metadata key
 * without a search index) hides itself instead of taking the whole page down with it.
 *
 * @param {Object} collection collection config
 * @param {Object} options passed to buildCollectionQueryParams ({ config, listing, excludeListingId })
 */
export const fetchListingCollection = (collection, options) => (dispatch, getState, sdk) => {
  const queryParams = buildCollectionQueryParams(collection, options);

  // Nothing to query on (e.g. a listing with no category and no author) - skip silently.
  if (!queryParams) {
    dispatch(fetchListingCollectionSuccess(collection.id, []));
    return Promise.resolve(null);
  }

  dispatch(fetchListingCollectionRequest(collection.id));

  return sdk.listings
    .query(queryParams)
    .then(response => {
      const sanitizeConfig = { listingFields: options?.config?.listing?.listingFields };
      dispatch(
        addMarketplaceEntities(
          withoutExcludedListing(response, options?.excludeListingId),
          sanitizeConfig
        )
      );

      // "Similar" is a broad query (top category level), so the closest matches are
      // ordered to the front here, before the collection is truncated to its limit.
      const listings = response.data.data;
      const ordered =
        collection.source === COLLECTION_SOURCES.SIMILAR
          ? rankBySimilarity(listings, options?.listing, options?.config)
          : listings;

      const listingIds = ordered.map(listing => listing.id);
      dispatch(fetchListingCollectionSuccess(collection.id, listingIds));
      return response;
    })
    .catch(e => {
      log.error(e, 'fetch-listing-collection-failed', { collectionId: collection.id });
      dispatch(fetchListingCollectionError(collection.id, storableError(e)));
      return null;
    });
};

/**
 * Fetch several collections in parallel. Safe to await in loadData.
 *
 * @param {Array} collections collection configs
 * @param {Object} options passed to each fetchListingCollection call
 */
export const fetchListingCollections = (collections = [], options) => dispatch =>
  Promise.all(collections.map(collection => dispatch(fetchListingCollection(collection, options))));
